// Need to use the React-specific entry point to import createApi
import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'

import {ENV} from '../app/Utils/_helpers/EnvHelper'
import {
  AppIntegrationResponse,
  BusinessNumberResponse,
  CreateTagParam,
  CreateTagResponse,
  CustomNumberResponse,
  DedicatedNumberResponse,
  DefaultMessageResponse,
  DeleteContactParams,
  SharedNumberResponse,
  GetListSharedNumberParams,
  DetailWebhookSecurityResponse,
  ExportSingleInboxResponse,
  GetGroupResponse,
  GetListDedicatedNumberParams,
  GetListDedicatedNumberResponse,
  GetSchedulerListResponse,
  GetTagResponse,
  GlobalSettingResponse,
  GlobalSettingUpdateResponse,
  MessageResponse,
  MongoAdminScaleResponse,
  SchedulerLogResponse,
  SchedulerResponse,
  SendInvoiceParams,
  SendInvoiceResponse,
  SubscribeParams,
  TestWebhookParams,
  TestWebhookResponse,
  TriggerMongoUpdateResponse,
  UnSubscribeParams,
  Update2FASettingResponse,
  UpdateAccountTypeResponse,
  UpdateSenderParams,
  UpdateEventTypeParams,
  UpdateWebhookParams,
  UploadHistoryResponse,
  UserOrderAdminListResponse,
  UserOrderAdminSummaryResponse,
  WriteWebhookParams,
  WriteWebhookSecurityResponse,
  CouponRate,
  UserSalesTargetResponse,
  CommissionFilter,
  SalesSummaryReport,
  UpdateBillingAddressResponse,
  PaginateResponse,
  GetAllUserAccountQueryParams,
  GetAllUserAccountResponse,
  SystemAlertActiveRequestParam,
  SystemAlertActiveResponse,
  SystemAlertRequestPayload,
  SystemAlertResponse,
  SystemAlertResponseData,
  UserSurvey,
  UserSurveyGraph,
  CustomerCoupons,
  SalesPerformance,
  PotentialChurnItems,
  HowToVideo,
  AdminDashboardQueryParams,
  AdminDashboardActivityOverviewData,
  AdminDashboardSalesContactsData,
  AdminDashboardBarGraphData,
} from './cellcast.type'
import {
  AppResponse,
  ExportResponse,
  DnGracePeriodSettingParam,
  DnGracePeriodSettingResponse,
  DnGracePeriodSetting,
  DedicatedNumberCustomPriceParam,
  DedicatedNumberCustomPriceResponse,
  DedicatedNumberCustomPrice,
} from './cellcast.v2.types'
import {Message} from './cellcast.v3.types'
import {
  GetConversationsParams,
  GetConversationsResponse,
  GetConversationMessagesParams,
  GetConversationMessagesResponse,
  GetConversationBadgeResponse,
  SearchConversationsParams,
  SearchConversationsResponse,
  UpdateConversationFlagsParams,
  UpdateFlagsResponse,
  BulkUpdateFlagsParams,
  GetConversationByIdParams,
  ApiConversation,
} from './cellcast.conversations.types'

const API_URL = ENV().REACT_APP_API_URL

// Define a service using a base URL and expected endpoints
export const cellcastApi = createApi({
  reducerPath: 'cellcastApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('token')
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }

      return headers
    },
  }),
  tagTypes: ['Schedulers'],
  endpoints: (builder) => ({
    getDashboardLeadGraph: builder.query({
      query: (query) => {
        return {
          url: `report/getDashboardLeadsGraph`,
          params: query,
        }
      },
    }),

    getDashboardActivityOverviewGraph: builder.query({
      query: (query) => {
        return {
          url: `report/getDashboardActivityOverviewGraph`,
          params: query,
        }
      },
    }),

    getDashboardQMAndAPIStatistic: builder.query({
      query: (query) => {
        return {
          url: `report/getDashboardQMAndAPIStatistic`,
          params: query,
        }
      },
    }),

    getDashboardMessageSentVsCreditCost: builder.query({
      query: (query) => {
        return {
          url: `report/getDashboardMessageSentVsCreditCost`,
          params: query,
        }
      },
    }),

    getDashboardGraph: builder.query({
      query: (query) => {
        return {
          url: `report/getDashboardGraph`,
          params: query,
        }
      },
    }),

    getDashboardRecentInboundMessages: builder.query({
      query: (query) => {
        return {
          url: `report/getDashboardRecentInboundMessages`,
          params: query,
        }
      },
    }),

    getDashboardClickCount: builder.query({
      query: (query) => {
        return {
          url: `report/getDashboardClickCount`,
          params: query,
        }
      },
    }),

    getAllCampaignListReport: builder.query({
      query: (query) => {
        return {
          url: `report/allCampaignListReport`,
          params: query,
        }
      },
    }),
    getAllCampaignReportExport: builder.query({
      query: (query) => {
        return {
          url: `report/allCampaignReportExport`,
          params: query,
        }
      },
    }),

    getDashboardCalendarData: builder.query({
      query: (query) => {
        return {
          url: `report/getDashboardCalendatData`,
          params: query,
        }
      },
    }),

    getDashboardEngagementAndRetentionGraph: builder.query({
      query: (query) => {
        return {
          url: `report/getDashboardEngagementAndRetentionGraph`,
          params: query,
        }
      },
    }),

    // support endpoint page & analytic
    getLeadGenerationData: builder.query({
      query: (query) => {
        return {
          url: `report/getLeadGenerationData`,
          params: query,
        }
      },
    }),

    getAllReportOverviewCount: builder.query({
      query: (query) => {
        return {
          url: `report/getActivityOverviewCount`,
          params: query,
        }
      },
    }),

    getQuickMsgReport: builder.query({
      query: (query) => {
        return {
          url: `report/allQuikMessageListReport`,
          params: query,
        }
      },
    }),

    getAPIQuickMsgReport: builder.query({
      query: (query) => ({
        url: `report/allQuikAPIMessageListReport`,
        params: query,
      }),
    }),

    cancelCampaign: builder.mutation({
      query: ({id, staggerMessages, staggerId}) => ({
        url: 'gateway/cancelScheduler',
        method: 'POST',
        body: {jobRefId: staggerMessages ? staggerId : id, staggerMessages},
      }),
    }),

    rescheduleCampaign: builder.mutation({
      query: ({id, staggerMessages, staggerId, scheduleAt, interval}) => ({
        url: 'gateway/rescheduleScheduler',
        method: 'POST',
        body: {jobRefId: staggerMessages ? staggerId : id, staggerMessages, scheduleAt, interval},
      }),
    }),

    getCards: builder.query<any, void>({
      query: () => 'user/getCards',
    }),

    // Subscribe And Unsubscribe
    unSubscribeContact: builder.mutation<any, UnSubscribeParams>({
      query: ({contactIDs}) => ({
        url: `group/contact/optout`,
        method: 'PUT',
        body: {ids: contactIDs},
      }),
    }),

    subscribeContact: builder.mutation<any, SubscribeParams>({
      query: ({contactIDs}) => ({
        url: `group/contact/subscribe`,
        method: 'PUT',
        body: {ids: contactIDs},
      }),
    }),

    // segments
    getAllSegments: builder.query({
      query: () => ({
        url: `segment/all`,
        method: 'GET',
      }),
    }),

    getSegmentContactWithPaginateMutate: builder.mutation({
      query: ({page, size, search, segmentId, isEmail}) => ({
        url: `segment/getSegmentContactsWithPagination/${segmentId}`,
        method: 'GET',
        params: {
          page: page || 1,
          size: size || 10,
          search: search || '',
        },
      }),
    }),

    attachTagToSegment: builder.mutation<
      AppResponse<string>,
      {
        segmentId: string
        tagIds: string[]
      }
    >({
      query: ({segmentId, tagIds}) => ({
        url: `segment/${segmentId}/tags`,
        method: 'POST',
        body: {
          tagIds,
        },
      }),
    }),

    // tags
    createTag: builder.mutation<CreateTagResponse, CreateTagParam>({
      query: (data) => ({
        url: `tags`,
        method: 'POST',
        body: data,
      }),
    }),

    getAllTags: builder.query<GetTagResponse, any>({
      query: () => ({
        url: `tags/tags-withought-pagination`,
        method: 'GET',
      }),
    }),

    // get contact
    getContactsByGroupMutate: builder.mutation({
      query: ({page, size, search, groupId, tagId, status}) => ({
        url: `mobile/group/data/${groupId}`,
        params: {
          page: page,
          size: size,
          search: search,
          tagId: tagId,
          status: status,
        },
      }),
    }),

    // delete contact
    deleteContactByGroup: builder.mutation<any, DeleteContactParams>({
      query: (arg) => ({
        url: 'group/deletemulticontact',
        body: arg,
        method: 'POST',
      }),
    }),

    // group
    getGroup: builder.query<GetGroupResponse, any>({
      query: () => ({
        url: 'mobile/group',
      }),
    }),
    // dedicated number transaction history
    getDedicatedNumberTransactions: builder.query<
      GetListDedicatedNumberResponse,
      GetListDedicatedNumberParams
    >({
      query: (params) => ({
        url: `user/get-user-orders/${params.userId}/dedicated-number`,
        params: {
          page: params.page,
          size: params.size,
          search: params.search,
          duration: params.duration,
          period: params.period,
        },
      }),
    }),

    verifyOtp: builder.mutation({
      query: (params) => ({
        url: 'auth/verify-email',
        method: 'POST',
        body: params,
      }),
    }),

    // bulk report
    getCampaignReportCustomerClicksListExport: builder.query({
      query: (params) => ({
        url: `report/getCampaignReportCustomerClicksListExport/${params.campId}`,
        params: {
          camp_type: params.campType,
        },
      }),
    }),

    getCampaignReportCustomerClicksList: builder.query({
      query: ({campId, params}) => ({
        url: `report/getCampaignReportCustomerClicksList/${campId}`,
        params,
      }),
    }),

    // bulk report customer export
    getCampaignReportCustomerOptoutExport: builder.query({
      query: (params) => ({
        url: `report/getCampaignReportCustomerOptout/${params.campId}`,
        params: {
          camp_type: params.campType,
          exportData: true,
        },
      }),
    }),

    getCampaignReportScheduleDetails: builder.query({
      query: (params) => ({
        url: `report/getCampaignReportScheduleDetails/${params.campId}`,
      }),
    }),

    getCampaignExports: builder.query({
      query: (params) => ({
        url: `report/getCampaigReportExports/${params.campId}`,
        params: {
          camp_type: params.campType,
        },
      }),
    }),

    updateOwnerStatusPageSubscribe: builder.mutation({
      query: (body) => ({
        url: `user/update-owner-status-page-subscribe`,
        body,
        method: 'POST',
      }),
    }),

    // File Scheduler
    createSchedulerFile: builder.mutation({
      query: (body) => ({
        url: 'scheduler/file',
        method: 'POST',
        body,
      }),
    }),

    createSummarySchedulerFile: builder.mutation({
      query: (body) => ({
        url: 'scheduler/file/summary',
        method: 'POST',
        body,
      }),
    }),

    getSchedulerFile: builder.query({
      query: (params) => ({
        url: 'scheduler/file',
        method: 'GET',
        params,
      }),
    }),

    deleteScheduler: builder.mutation({
      query: (params) => ({
        url: `scheduler/group/${params.id}`,
        method: 'DELETE',
      }),
    }),

    deleteFileScheduler: builder.mutation({
      query: (params) => ({
        url: `scheduler/file/${params.id}`,
        method: 'DELETE',
      }),
    }),

    pauseScheduler: builder.mutation({
      query: (params) => ({
        url: `scheduler/pause/${params.id}`,
        method: 'PUT',
      }),
    }),

    getSchedulerLog: builder.query<SchedulerLogResponse, any>({
      query: (params) => ({
        url: `scheduler/log/${params.id}`,
        method: 'GET',
      }),
    }),

    getSchedulerHistory: builder.query<MessageResponse, any>({
      query: (params) => ({
        url: `scheduler/history/${params.id}`,
        method: 'GET',
      }),
    }),

    getSchedulers: builder.query<GetSchedulerListResponse, any>({
      query: (params) => ({
        url: `scheduler/group`,
        method: 'GET',
        params: params,
      }),
      providesTags: ['Schedulers'],
    }),

    getSchedulerDetail: builder.query<SchedulerResponse, any>({
      query: (params) => ({
        url: `scheduler/group/${params.id}`,
        method: 'GET',
      }),
    }),

    getSenderNumber: builder.query({
      queryFn: async (arg, queryApi, extraOptions, baseQuery) => {
        try {
          const [response1, response2, response3] = await Promise.all<any>([
            baseQuery('/business'),
            baseQuery('/user/get-dedicated-number'),
            baseQuery('/customNumber'),
          ])

          if (response1?.error || response2?.error || response3?.error) {
            return {error: response1.error || response2.error || response3.error}
          }

          const businessName = response1?.data?.data.map((it: any) => ({
            value: `businessName_${it?.businessname}`,
            label: `${it?.businessname}`,
            group: 'business',
            isDefault: it?.isDefault,
          }))

          const dedicatedNumber = response2?.data?.data.map((it: any) => ({
            value: `dedicatedNumber_${it?.audNumber}`,
            label: `${it?.audNumber}`,
            group: 'dedicated',
            isDefault: it?.isDefault,
          }))

          const customNumber = response3?.data?.data.map((it: any) => ({
            value: `customNumber_${it?.number}`,
            label: `${it?.number}`,
            group: 'custom',
            isDefault: it?.isDefault,
          }))

          const groupedList = [
            {
              label: 'Shared Number',
              options: [{value: '#SharedNum#', label: '#SharedNum#'}],
            },
            {
              label: 'Business Name',
              options: businessName,
            },
            {
              label: 'Dedicated Number',
              options: dedicatedNumber,
            },
            {
              label: 'Custom Number',
              options: customNumber,
            },
          ]

          const getDefaultNumber = groupedList
            .flatMap((it) => it.options)
            .find((it) => it.isDefault)
          // Combine the results
          return {
            data: {
              list: groupedList,
              defaultNumber: getDefaultNumber,
            },
          }
        } catch (error) {
          return {error: error instanceof Error ? error.message : 'Unknown error'}
        }
      },
    }),

    getUploadHistories: builder.query<UploadHistoryResponse, any>({
      query: (params) => ({
        url: `uploadHistory/getAllReports`,
        method: 'GET',
        params,
      }),
    }),

    updateAccountTypeRequest: builder.mutation<UpdateAccountTypeResponse, any>({
      query: (params) => ({
        url: `user/accountTypeChangeRequest`,
        method: 'POST',
        body: params,
      }),
    }),

    sendDraftInvoiceToCustomer: builder.mutation<SendInvoiceResponse, SendInvoiceParams>({
      query: (params) => ({
        url: 'user/senddraftinvoicetocustomer',
        method: 'POST',
        body: params,
      }),
    }),

    // webhook security
    createWebhookSecurity: builder.mutation<WriteWebhookSecurityResponse, WriteWebhookParams>({
      query: (payload) => {
        return {
          url: 'user/webhook/security',
          method: 'POST',
          body: payload,
        }
      },
    }),

    updateWebhookSecurity: builder.mutation<WriteWebhookSecurityResponse, WriteWebhookParams>({
      query: (payload) => {
        return {
          url: 'user/webhook/security',
          method: 'PUT',
          body: payload,
        }
      },
    }),

    getWebhookSecurity: builder.query<DetailWebhookSecurityResponse, null>({
      query: (payload) => {
        return {
          url: 'user/webhook/security',
          method: 'GET',
        }
      },
    }),

    testWebhook: builder.mutation<TestWebhookResponse, TestWebhookParams>({
      query: (payload) => {
        return {
          url: 'user/webhook/sendRequest',
          method: 'POST',
          body: payload,
        }
      },
    }),

    update2FASetting: builder.mutation<Update2FASettingResponse, any>({
      query: (payload) => {
        return {
          url: 'user/update2FASetting',
          method: 'POST',
          body: payload,
        }
      },
    }),

    updateBillingAddres: builder.mutation<UpdateBillingAddressResponse, any>({
      query: (payload) => {
        return {
          url: 'user/updateBillingAddress',
          method: 'POST',
          body: payload,
        }
      },
    }),

    getCreditTransactionExportData: builder.query({
      query: (params) => {
        return {
          url: 'creditTransactions/exportData',
          method: 'GET',
          params,
        }
      },
    }),

    getSingleReceiverMessageExport: builder.query<ExportSingleInboxResponse, any>({
      query: (params) => {
        return {
          url: 'gateway',
          method: 'GET',
          params,
        }
      },
    }),

    adminUpdateGlobalSetting: builder.mutation<GlobalSettingUpdateResponse, any>({
      query: (params) => {
        return {
          url: 'admin/updateGlobalSetting',
          method: 'POST',
          body: params,
        }
      },
    }),

    getAdminMongoScale: builder.query<MongoAdminScaleResponse, any>({
      query: (params) => {
        return {
          url: 'admin/mongoScale',
          method: 'GET',
          params,
        }
      },
    }),

    postAdminMongoScale: builder.mutation<TriggerMongoUpdateResponse, any>({
      query: (body) => {
        return {
          url: 'admin/mongoScale',
          method: 'POST',
          body,
        }
      },
    }),

    adminGetGlobalSetting: builder.query<GlobalSettingResponse, any>({
      query: () => {
        return {
          url: 'admin/getGlobalSetting',
          method: 'GET',
        }
      },
    }),

    // ghl
    updateGhlSender: builder.mutation<AppResponse<DefaultMessageResponse>, UpdateSenderParams>({
      query: (payload) => {
        return {
          url: 'ghl/sender',
          method: 'PATCH',
          body: payload,
        }
      },
    }),
    importGhlContact: builder.mutation<AppResponse<DefaultMessageResponse>, null>({
      query: (payload) => {
        return {
          url: 'ghl/contact/import',
          method: 'POST',
        }
      },
    }),
    exportGhlContact: builder.mutation<AppResponse<DefaultMessageResponse>, null>({
      query: (payload) => {
        return {
          url: 'ghl/contact/export',
          method: 'POST',
        }
      },
    }),

    importJobAdderContact: builder.mutation<AppResponse<DefaultMessageResponse>, null>({
      query: (payload) => {
        return {
          url: 'jobadder/contact/import',
          method: 'POST',
        }
      },
    }),
    exportJobAdderContact: builder.mutation<AppResponse<DefaultMessageResponse>, null>({
      query: (payload) => {
        return {
          url: 'jobadder/contact/export',
          method: 'POST',
        }
      },
    }),

    importHubSpotContact: builder.mutation<AppResponse<DefaultMessageResponse>, null>({
      query: (payload) => {
        return {
          url: 'hubspot/contact/import',
          method: 'POST',
        }
      },
    }),
    exportHubSpotContact: builder.mutation<AppResponse<DefaultMessageResponse>, null>({
      query: (payload) => {
        return {
          url: 'hubspot/contact/export',
          method: 'POST',
        }
      },
    }),

    // activecampaign
    updateActiveCampaignSender: builder.mutation<AppResponse<DefaultMessageResponse>, UpdateSenderParams>({
      query: (payload) => {
        return {
          url: 'activecampaign/sender',
          method: 'PATCH',
          body: payload,
        }
      },
    }),
    importActiveCampaignContact: builder.mutation<AppResponse<DefaultMessageResponse>, null>({
      query: (payload) => {
        return {
          url: 'activecampaign/import-contacts',
          method: 'POST',
        }
      },
    }),
    exportActiveCampaignContact: builder.mutation<AppResponse<DefaultMessageResponse>, null>({
      query: (payload) => {
        return {
          url: 'activecampaign/export-contacts',
          method: 'POST',
        }
      },
    }),

    updateEventType: builder.mutation<AppResponse<DefaultMessageResponse>, UpdateEventTypeParams>({
      query: (payload) => {
        return {
          url: 'jobadder/eventtype',
          method: 'PATCH',
          body: payload,
        }
      },
    }),

    updateWebhook: builder.mutation<AppResponse<DefaultMessageResponse>, UpdateWebhookParams>({
      query: (payload) => {
        return {
          url: 'jobadder/webhook',
          method: 'POST',
          body: payload,
        }
      },
    }),

    syncPartnerAction: builder.mutation<AppResponse<DefaultMessageResponse>, any>({
      query: (payload) => {
        return {
          url: 'jobadder/sync',
          method: 'POST',
          body: payload,
        }
      },
    }),

    // dedicated number
    getDedicatedNumber: builder.query<DedicatedNumberResponse, any>({
      query: () => {
        return {
          url: '/user/get-dedicated-number',
          method: 'GET',
        }
      },
    }),
    getUserOrderAdminSummary: builder.query<
      UserOrderAdminSummaryResponse,
      {userId: string; fromDate: string; toDate: string}
    >({
      query: ({userId, ...params}) => {
        return {
          url: `user/get-user-orders-admin-summary/${userId}`,
          method: 'GET',
          params,
        }
      },
    }),

    getUserOrderAdminList: builder.query<
      UserOrderAdminListResponse,
      {userId: string; fromDate: string; toDate: string; page: number; size: number}
    >({
      query: ({userId, ...params}) => {
        return {
          url: `user/get-user-orders-admin-list/${userId}`,
          method: 'GET',
          params,
        }
      },
    }),

    exportUserOrderAdminList: builder.query<
      any,
      {userId: string; fromDate: string; toDate: string}
    >({
      query: ({...params}) => {
        return {
          url: `user/commission/export`,
          method: 'GET',
          params,
        }
      },
    }),

    adminUpdateCommission: builder.mutation<AppResponse<null>, any>({
      query: (body) => {
        return {
          url: `admin/updateCommission`,
          method: 'PUT',
          body,
        }
      },
    }),

    activateIntegrationApp: builder.mutation<
      AppIntegrationResponse,
      {integrationProviderName: string}
    >({
      query: (body) => {
        return {
          url: 'integration-app/activate',
          method: 'POST',
          body,
        }
      },
    }),

    deactivateIntegrationApp: builder.mutation<
      AppIntegrationResponse,
      {integrationProviderName: string}
    >({
      query: (body) => {
        return {
          url: 'integration-app/deactivate',
          method: 'POST',
          body,
        }
      },
    }),

    updateIntegrationAppData: builder.mutation<AppIntegrationResponse, any>({
      query: (body) => {
        return {
          url: 'integration-app/update-data',
          method: 'POST',
          body,
        }
      },
    }),

    adminUpdateUserCOG: builder.mutation<AppResponse<null>, any>({
      query: ({userId, ...body}) => {
        return {
          url: `admin/updateUserCOG/${userId}`,
          method: 'PUT',
          body,
        }
      },
    }),

    adminUpdateBulkGatewayMessage: builder.mutation<AppResponse<null>, any>({
      query: (body) => {
        return {
          url: 'admin/updateBulkGatewayMessage',
          method: 'PUT',
          body,
        }
      },
    }),

    getGatewayProvider: builder.query<AppResponse<any>, null>({
      query: () => {
        return {
          url: 'admin/getGatewayProvider',
          method: 'GET',
        }
      },
    }),

    getAdminGatewayProvider: builder.query<AppResponse<any>, null>({
      query: () => {
        return {
          url: 'admin/gatewayProvider',
          method: 'GET',
        }
      },
    }),

    createGatewayProvider: builder.mutation<AppResponse<any>, any>({
      query: (body) => {
        return {
          url: 'admin/createGatewayProvider',
          method: 'POST',
          body,
        }
      },
    }),

    assignListToAnotherList: builder.mutation<AppResponse<null>, any>({
      query: (body) => {
        return {
          url: 'group/assignListToAnotherList',
          method: 'POST',
          body,
        }
      },
    }),

    moveListToAnotherList: builder.mutation<AppResponse<null>, any>({
      query: (body) => {
        return {
          url: 'group/moveListToAnotherList',
          method: 'POST',
          body,
        }
      },
    }),

    getFileSchedulerMessageExport: builder.query<ExportResponse, any>({
      query: (params) => ({
        url: 'scheduler/group',
        method: 'GET',
        params: params,
      }),
    }),

    notificationMarkAllReads: builder.mutation<AppResponse<null>, {}>({
      query: (body) => {
        return {
          url: 'notification/mark-as-reads',
          method: 'PUT',
        }
      },
    }),

    getNotificationDetail: builder.query<any, any>({
      query: ({id}) => {
        return {
          url: `notification/${id}`,
          method: 'GET',
        }
      },
    }),

    getAllApprovalNotification: builder.query<
      AppResponse<any>,
      {
        page: number
        size: number
      }
    >({
      query: (params) => {
        return {
          url: '/notification/all-approval',
          method: 'GET',
          params: params,
        }
      },
    }),

    exportAllApprovalNotification: builder.query<AppResponse<any>, any>({
      query: () => {
        return {
          url: `/notification/all-approval/export`,
          method: 'GET',
        }
      },
    }),

    adminUpdateSmsStatus: builder.mutation<
      AppResponse<null>,
      {
        userId: string
        status: string
      }
    >({
      query: (body) => {
        return {
          url: '/admin/user/sms-status',
          method: 'PATCH',
          body,
        }
      },
    }),

    adminUpdateUserTag: builder.mutation<
      AppResponse<null>,
      {userId?: string; tag?: string; action?: string}
    >({
      query: (body) => {
        return {
          url: '/admin/user/tag',
          method: 'PATCH',
          body,
        }
      },
    }),

    adminUpdateUserVerifiedEmail: builder.mutation<
      AppResponse<null>,
      {userId?: string; status?: boolean}
    >({
      query: (body) => {
        return {
          url: '/admin/user/email-verified',
          method: 'PATCH',
          body,
        }
      },
    }),

    adminUpdateSalesType: builder.mutation<
      AppResponse<null>,
      {
        userId?: string
        salesType?: string
        notificationId?: string
        status?: string
      }
    >({
      query: (body) => {
        return {
          url: '/admin/user/sales-type',
          method: 'PATCH',
          body,
        }
      },
    }),

    adminAssignedUserbySuperAdmin: builder.mutation<AppResponse<null>, any>({
      query: (body) => {
        return {
          url: 'admin/assignedUserbySuperAdmin',
          method: 'POST',
          body,
        }
      },
    }),

    // business number
    getBusinessNumber: builder.query<BusinessNumberResponse, any>({
      query: () => {
        return {
          url: '/business',
          method: 'GET',
        }
      },
    }),

    getCustomNumber: builder.query<CustomNumberResponse, any>({
      query: () => {
        return {
          url: '/customNumber',
          method: 'GET',
        }
      },
    }),

    // bounce notification
    updateBounceNotificationSetting: builder.mutation<
      AppResponse<any>,
      {
        bounceThresholdEnable: boolean
        bounceThreshold: number
        bounceNotificationEmail: boolean
        bounceNotificationSms: boolean
      }
    >({
      query: (params) => {
        return {
          url: '/user/bounce-notification-setting',
          method: 'PUT',
          body: {
            bounceNotificationSettings: params,
          },
        }
      },
    }),

    // unsubscribe
    updateUnsubscribeNotificationSetting: builder.mutation<
      AppResponse<any>,
      {
        unsubscribeThresholdEnable: boolean
        unsubscribeThreshold: number
        unsubscribeNotificationEmail: boolean
        unsubscribeNotificationSms: boolean
      }
    >({
      query: (params) => {
        return {
          url: '/user/unsubscribe-notification-setting',
          method: 'PUT',
          body: {
            unsubscribeNotificationSettings: params,
          },
        }
      },
    }),

    // notification settings (new bite & voice)
    updateNotificationSettings: builder.mutation<
      AppResponse<any>,
      {
        newBiteNotificationSettings?: {
          newBiteNotificationEmail: boolean
          newBiteNotificationSms: boolean
          newBiteBellNotification: boolean
        }
        voiceNotificationSettings?: {
          voiceBellNotification: boolean
          voiceNotificationEmail: boolean
          voiceNotificationSms: boolean
        }
      }
    >({
      query: (params) => {
        return {
          url: '/user/notification-settings',
          method: 'PUT',
          body: params,
        }
      },
    }),

    // default coupon rate
    getDefaultCouponRate: builder.query<AppResponse<CouponRate[]>, any>({
      query: () => {
        return {
          url: '/user/default-coupon-rate',
          method: 'POST',
        }
      },
    }),

    getCustomerCoupons: builder.query<AppResponse<CustomerCoupons[]>, {userId: string}>({
      query: ({userId, ...params}) => {
        return {
          url: `user/coupon/user/${userId}`,
          method: 'GET',
          params,
        }
      },
    }),

    deleteCostumerCoupons: builder.mutation<CustomerCoupons, {userId: string; couponId: string}>({
      query: ({userId, couponId}) => ({
        url: `user/coupon/${couponId}/assign/${userId}`,
        method: 'DELETE',
      }),
    }),

    assignCostumerCoupons: builder.mutation<AppResponse<CustomerCoupons>, any>({
      query: (body) => {
        return {
          url: 'user/coupon/assign',
          method: 'POST',
          body,
        }
      },
    }),

    // sales KPI
    getSalesPerformanceList: builder.query<AppResponse<SalesPerformance>, any>({
      query: (params) => {
        return {
          url: `user/sales/performance`,
          method: 'GET',
          params: params,
        }
      },
    }),

    getSalesPotentialChurnUser: builder.query<AppResponse<PotentialChurnItems>, any>({
      query: (params) => {
        return {
          url: `user/sales/potential-churn`,
          method: 'GET',
          params: params,
        }
      },
    }),

    updateStatusUsedCoupon: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      {userId: string; hasUsedCoupon: boolean}
    >({
      query: (payload) => {
        return {
          url: 'user/coupon/used-coupon',
          method: 'PATCH',
          body: {
            userId: payload.userId,
            hasUsedCoupon: payload.hasUsedCoupon,
          },
        }
      },
    }),

    updateHubspotSender: builder.mutation<AppResponse<DefaultMessageResponse>, UpdateSenderParams>({
      query: (payload) => {
        return {
          url: 'hubspot/sender',
          method: 'PATCH',
          body: payload,
        }
      },
    }),

    updatePhoneNumberField: builder.mutation<AppResponse<DefaultMessageResponse>, string>({
      query: (payload) => {
        return {
          url: 'hubspot/phone-number-field',
          method: 'PATCH',
          body: {
            phoneNumberField: payload,
          },
        }
      },
    }),

    getUserSalesTarget: builder.query<UserSalesTargetResponse, {userId: string}>({
      query: ({userId, ...params}) => {
        return {
          url: `user/get-user-orders-admin-list/${userId}/sales-target`,
          method: 'GET',
          params,
        }
      },
    }),

    updateUserSalesTarget: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      {userId: string; target: number}
    >({
      query: ({userId, target}) => {
        return {
          url: `user/get-user-orders-admin-list/${userId}/sales-target`,
          method: 'PUT',
          body: {
            salesTarget: target,
          },
        }
      },
    }),

    getSalesCommissionList: builder.query<AppResponse<SalesSummaryReport>, CommissionFilter>({
      query: (params) => {
        return {
          url: `user/commission/list`,
          method: 'GET',
          params: params,
        }
      },
    }),

    // QM
    cancelScheduleQuickMessage: builder.mutation<
      AppResponse<{message: string}>,
      {messageId: string; type: string}
    >({
      query: ({messageId, type}) => ({
        url: `gateway/cancelScheduleQuickMessage`,
        method: 'POST',
        body: {
          messageId,
          type,
        },
      }),
    }),

    sendQM: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      {
        sender: string
        message: string
        senderType: string
        contacts: string[]
        longUrl: []
        countryCode: string
        scheduleAt?: Date | string
      }
    >({
      query: (payload) => ({
        url: `gateway`,
        method: 'POST',
        body: payload,
      }),
    }),

    getInboxMessages: builder.query<
      AppResponse<PaginateResponse<Message>>,
      {
        sortField: string
        sortOrder: string
        receiver: string
        page: number
        limit: number
      }
    >({
      query: (payload) => ({
        url: `gateway`,
        method: 'GET',
        params: payload,
      }),
    }),

    getInboxMessageDetails: builder.query<Message, string>({
      query: (messageId) => ({
        url: `gateway/messages/${messageId}`,
        method: 'GET',
      }),
    }),

    // ==================== NEW CONVERSATION API ENDPOINTS ====================
    // These use the new /conversations endpoint for improved performance

    /**
     * GET /conversations - List inbox conversations with filtering, sorting, and pagination
     */
    getConversations: builder.query<AppResponse<GetConversationsResponse>, GetConversationsParams>({
      query: (params) => ({
        url: 'conversations',
        method: 'GET',
        params,
      }),
    }),

    /**
     * GET /conversations/badge - Get unread conversation count
     */
    getConversationBadge: builder.query<AppResponse<GetConversationBadgeResponse>, void>({
      query: () => ({
        url: 'conversations/badge',
        method: 'GET',
      }),
    }),

    /**
     * GET /conversations/search - Full-text search across conversations and messages
     */
    searchConversations: builder.query<
      AppResponse<SearchConversationsResponse>,
      SearchConversationsParams
    >({
      query: (params) => ({
        url: 'conversations/search',
        method: 'GET',
        params,
      }),
    }),

    /**
     * GET /conversations/:id - Get a single conversation with full details
     */
    getConversationById: builder.query<AppResponse<ApiConversation>, GetConversationByIdParams>({
      query: ({id, lookupBy}) => ({
        url: `conversations/${id}`,
        method: 'GET',
        params: lookupBy ? {lookupBy} : undefined,
      }),
    }),

    /**
     * GET /conversations/:id/messages - Get messages for a specific conversation
     */
    getConversationMessages: builder.query<
      AppResponse<GetConversationMessagesResponse>,
      GetConversationMessagesParams
    >({
      query: ({conversationId, ...params}) => ({
        url: `conversations/${conversationId}/messages`,
        method: 'GET',
        params,
      }),
    }),

    /**
     * PATCH /conversations/:id/flags - Update flags on a single conversation
     */
    updateConversationFlags: builder.mutation<
      AppResponse<UpdateFlagsResponse>,
      UpdateConversationFlagsParams
    >({
      query: ({conversationId, flags}) => ({
        url: `conversations/${conversationId}/flags`,
        method: 'PATCH',
        body: flags,
      }),
    }),

    /**
     * PATCH /conversations/flags - Bulk update flags on multiple conversations
     */
    bulkUpdateConversationFlags: builder.mutation<
      AppResponse<UpdateFlagsResponse>,
      BulkUpdateFlagsParams
    >({
      query: (body) => ({
        url: 'conversations/flags',
        method: 'PATCH',
        body,
      }),
    }),

    // ==================== END CONVERSATION API ENDPOINTS ====================

    getAllInprogressCampaignCount: builder.query<AppResponse<any>, any>({
      query: (params) => ({
        url: `report/allInprogressCampaignCount`,
        method: 'GET',
        params: params,
      }),
    }),

    // How-to-videos endpoints
    getHowToVideos: builder.query({
      query: (params) => ({
        url: 'how-to-videos',
        method: 'GET',
        params: params,
      }),
    }),

    getHowToVideosAdmin: builder.query({
      query: (params) => ({
        url: 'how-to-videos/admin',
        method: 'GET',
        params: params,
      }),
    }),

    getHowToVideoById: builder.query({
      query: (id) => ({
        url: `how-to-videos/${id}`,
        method: 'GET',
      }),
    }),

    createHowToVideo: builder.mutation<any, any>({
      query: (data) => ({
        url: 'how-to-videos',
        method: 'POST',
        body: data,
      }),
    }),

    updateHowToVideo: builder.mutation<any, any>({
      query: ({id, ...data}) => ({
        url: `how-to-videos/${id}`,
        method: 'PUT',
        body: data,
      }),
    }),

    deleteHowToVideo: builder.mutation<any, string>({
      query: (id) => ({
        url: `how-to-videos/${id}`,
        method: 'DELETE',
      }),
    }),

    toggleHowToVideoStatus: builder.mutation<any, {id: string; isActive: boolean}>({
      query: ({id, isActive}) => ({
        url: `how-to-videos/${id}/status`,
        method: 'PATCH',
        body: {isActive},
      }),
    }),

    getHowToVideosByFeature: builder.query<AppResponse<HowToVideo[]>, {feature: string}>({
      query: ({feature}) => ({
        url: `how-to-videos/feature/${feature}`,
        method: 'GET',
      }),
    }),

    // system alert
    createSystemAlert: builder.mutation<SystemAlertResponse, SystemAlertRequestPayload>({
      query: (params) => ({
        url: `notification/system-alert`,
        method: 'POST',
        body: params,
      }),
    }),

    getActiveSystemAlert: builder.query<SystemAlertActiveResponse, SystemAlertActiveRequestParam>({
      query: () => ({
        url: `notification/system-alert/active`,
        method: 'GET',
      }),
    }),

    getAllSystemAlert: builder.query<
      AppResponse<PaginateResponse<SystemAlertResponseData>>,
      {
        startAt?: string | null
        endAt?: string | null
        methods?: string
        page: number
        limit: number
      }
    >({
      query: (payload) => ({
        url: `notification/system-alert-all`,
        method: 'GET',
        params: payload,
      }),
    }),

    deleteSystemAlert: builder.query<
      AppResponse<PaginateResponse<SystemAlertResponseData>>,
      {
        id: String
      }
    >({
      query: (payload) => ({
        url: `notification/system-alert`,
        method: 'DELETE',
        params: payload,
      }),
    }),

    // user
    getAllUserAccount: builder.query<GetAllUserAccountResponse, GetAllUserAccountQueryParams>({
      query: (params = {}) => ({
        url: 'admin/getAllUserAccount',
        method: 'GET',
        params,
      }),
    }),

    // user
    getAllUserSurvey: builder.query<
      AppResponse<PaginateResponse<UserSurvey>>,
      {
        page: number
        limit: number
      }
    >({
      query: (payload) => ({
        url: `user/feedback/survey-all`,
        method: 'GET',
        params: payload,
      }),
    }),

    // user
    getAllUserSurveyGraph: builder.query<AppResponse<UserSurveyGraph[]>, any>({
      query: () => ({
        url: `user/feedback/survey-graph`,
        method: 'GET',
      }),
    }),

    sendInviteLink: builder.mutation<AppResponse<any>, any>({
      query: (payload) => ({
        url: 'invite-link/send-invite',
        method: 'POST',
        body: payload,
      }),
    }),

    verifyInviteToken: builder.mutation<AppResponse<any>, any>({
      query: (payload) => ({
        url: 'invite-link/verify-token',
        method: 'POST',
        body: payload,
      }),
    }),

    setupAccountFromInvite: builder.mutation<AppResponse<any>, any>({
      query: (payload) => ({
        url: 'invite-link/setup-account',
        method: 'POST',
        body: payload,
      }),
    }),

    genererateInviteLink: builder.mutation<AppResponse<any>, any>({
      query: (payload) => ({
        url: 'invite-link/generate-invite-link',
        method: 'POST',
        body: payload,
      }),
    }),
    updateSoundNotification: builder.mutation<
      AppResponse<any>,
      {
        newNotification: boolean
        newInbox: boolean
      }
    >({
      query: (params) => {
        return {
          url: '/user/notification-sound',
          method: 'POST',
          body: params,
        }
      },
    }),

    getSoundNotificationSetting: builder.query<
      AppResponse<{newInbox: boolean; newNotification: boolean}>,
      any
    >({
      query: () => ({
        url: `user/notification-sound`,
        method: 'GET',
      }),
    }),

    getOptOutFraudKeywords: builder.query<AppResponse<{fraud_keywords: string}>, any>({
      query: () => ({
        url: `user/fraud-keywords/opt-out`,
        method: 'GET',
      }),
    }),

    updateOptOutFraudKeywords: builder.mutation<
      AppResponse<any>,
      {
        keywords: string
      }
    >({
      query: (params) => {
        return {
          url: `user/fraud-keywords/opt-out`,
          method: 'PATCH',
          body: params,
        }
      },
    }),
    getSharedNumberList: builder.query<SharedNumberResponse, GetListSharedNumberParams>({
      query: (params) => ({
        url: 'admin/sharednumber',
        method: 'GET',
        params,
      }),
    }),

    createSharedNumber: builder.mutation<AppResponse<any>, any>({
      query: (payload) => ({
        url: 'admin/sharednumber',
        method: 'POST',
        body: payload,
      }),
    }),
    // authentication domain
    authenticateDomain: builder.mutation<AppResponse<{domain: string}>, any>({
      query: (payload) => ({
        url: `email-domain/${payload.id}`,
        method: 'POST',
        body: payload,
      }),
    }),

    updateSharedNumber: builder.mutation<AppResponse<any>, any>({
      query: (payload) => ({
        url: `admin/sharednumber/${payload._id}`,
        method: 'PUT',
        body: payload,
      }),
    }),

    deleteSharedNumber: builder.mutation<AppResponse<any>, any>({
      query: (payload) => ({
        url: `admin/sharednumber/${payload.id}`,
        method: 'DELETE',
      }),
    }),

    // Gateway Configs
    getGatewayConfigs: builder.query<AppResponse<any>, any>({
      query: (params) => ({
        url: 'admin/getGatewayList',
        method: 'GET',
        params,
      }),
    }),

    createGatewayConfig: builder.mutation<
      AppResponse<{success: boolean; gatewayId: string; message: string}>,
      any
    >({
      query: (payload) => ({
        url: 'admin/addGatewayConfig',
        method: 'POST',
        body: payload,
      }),
    }),

    updateGatewayConfig: builder.mutation<
      AppResponse<{success: boolean; message: string}>,
      {id: string; [key: string]: any}
    >({
      query: ({id, ...payload}) => ({
        url: `admin/updateGatewayConfig/${id}`,
        method: 'PUT',
        body: payload,
      }),
    }),

    deleteGatewayConfig: builder.mutation<AppResponse<any>, any>({
      query: (payload) => ({
        url: `gateway-configs/${payload.id}`,
        method: 'DELETE',
      }),
    }),

    // User Profile
    getUserProfileDetails: builder.query<AppResponse<any>, string>({
      query: (userId) => ({
        url: `user/getuserdetails/${userId}`,
        method: 'GET',
      }),
    }),

    // DN Grace Period Setting
    getDnGracePeriodSetting: builder.query<DnGracePeriodSettingResponse, DnGracePeriodSettingParam>(
      {
        query: ({userId}) => ({
          url: `user/dn-grace-period-setting/${userId}`,
          method: 'GET',
        }),
      }
    ),
    updateDnGracePeriodSetting: builder.mutation<
      DnGracePeriodSettingResponse,
      {userId: string; data: DnGracePeriodSetting}
    >({
      query: ({userId, data}) => ({
        url: `user/dn-grace-period-setting/${userId}`,
        method: 'PUT',
        body: data,
      }),
    }),

    // DN Custom Price Setting (User facing - for purchase modal)
    getDnCustomPriceSetting: builder.query<
      AppResponse<DedicatedNumberCustomPrice>,
      DedicatedNumberCustomPriceParam
    >({
      query: ({userId}) => ({
        url: `user/dn-custom-price-setting/${userId}`,
        method: 'GET',
      }),
    }),

    // Update user billing DN type (postpaid/prepaid)
    updateUserBillingDNType: builder.mutation<
      AppResponse<{message: string}>,
      {userId: string; billingDedicatedNumberType: '' | 'postpaid' | 'prepaid'}
    >({
      query: ({userId, billingDedicatedNumberType}) => ({
        url: `admin/updateUserBillingDNType/${userId}`,
        method: 'PUT',
        body: {billingDedicatedNumberType},
      }),
    }),

    // Dedicated Number Custom Price
    getDedicatedNumberCustomPrice: builder.query<
      DedicatedNumberCustomPriceResponse,
      DedicatedNumberCustomPriceParam
    >({
      query: ({userId}) => ({
        url: `admin/dedicatedNumberCustomPrice/${userId}`,
        method: 'GET',
      }),
    }),
    updateDedicatedNumberCustomPrice: builder.mutation<
      DedicatedNumberCustomPriceResponse,
      {userId: string; data: DedicatedNumberCustomPrice}
    >({
      query: ({userId, data}) => ({
        url: `admin/dedicatedNumberCustomPrice/${userId}`,
        method: 'PUT',
        body: data,
      }),
    }),
    deleteDedicatedNumberCustomPrice: builder.mutation<
      DedicatedNumberCustomPriceResponse,
      DedicatedNumberCustomPriceParam
    >({
      query: ({userId}) => ({
        url: `admin/dedicatedNumberCustomPrice/${userId}`,
        method: 'DELETE',
      }),
    }),

    // Custom Opt-Out Page Branding
    getCustomOptOut: builder.query<
      AppResponse<{customLogo: string; customTitle: string; customFooter: string}>,
      {userId: string}
    >({
      query: ({userId}) => ({
        url: `user/custom-opt-out/${userId}`,
        method: 'GET',
      }),
    }),
    updateCustomOptOut: builder.mutation<
      AppResponse<{customLogo: string; customTitle: string; customFooter: string}>,
      {userId: string; formData: FormData}
    >({
      query: ({userId, formData}) => ({
        url: `user/custom-opt-out/${userId}`,
        method: 'PUT',
        body: formData,
      }),
    }),

    // Gateway to Conversations Migration
    migrateGatewayToConversations: builder.mutation<
      AppResponse<any>,
      {userIds: string[]; dateFrom: string; dateTo: string}
    >({
      query: (data) => ({
        url: 'admin/migration/gateway-to-conversations',
        method: 'POST',
        body: data,
      }),
    }),

    // Admin Dashboard endpoints with RTK Query caching
    getAdminDashboardActivityOverviewCount: builder.query<
      AppResponse<AdminDashboardActivityOverviewData>,
      AdminDashboardQueryParams
    >({
      query: (params) => ({
        url: 'report/getAdminDashboardActivityOverviewCount',
        method: 'GET',
        params,
      }),
    }),

    getAdminDashboardSalesContactsCount: builder.query<
      AppResponse<AdminDashboardSalesContactsData>,
      AdminDashboardQueryParams
    >({
      query: (params) => ({
        url: 'report/getAdminDashboardSalesContactsCount',
        method: 'GET',
        params,
      }),
    }),

    getAdminDashboardMessageSentWithNewClientsBarGraph: builder.query<
      AppResponse<AdminDashboardBarGraphData>,
      AdminDashboardQueryParams
    >({
      query: (params) => ({
        url: 'report/getAdminDashboardMessageSentWithNewClientsBarGraph',
        method: 'GET',
        params,
      }),
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetDashboardLeadGraphQuery,
  useGetDashboardActivityOverviewGraphQuery,
  useGetDashboardQMAndAPIStatisticQuery,
  useGetDashboardMessageSentVsCreditCostQuery,
  useGetDashboardGraphQuery,
  useGetDashboardRecentInboundMessagesQuery,
  useGetDashboardClickCountQuery,
  useGetAllCampaignListReportQuery,
  useLazyGetAllCampaignListReportQuery,
  useLazyGetAllCampaignReportExportQuery,
  useGetDashboardCalendarDataQuery,
  useGetDashboardEngagementAndRetentionGraphQuery,
  useGetQuickMsgReportQuery,
  useGetAPIQuickMsgReportQuery,
  useGetCardsQuery,
  useGetAllSegmentsQuery,
  useGetAllTagsQuery,
  useLazyGetAllTagsQuery,
  useGetGroupQuery,
  useLazyGetGroupQuery,
  useLazyGetDedicatedNumberTransactionsQuery,
  useLazyGetCampaignReportCustomerClicksListExportQuery,
  useLazyGetCampaignReportCustomerOptoutExportQuery,
  useGetCampaignReportScheduleDetailsQuery,
  useLazyGetCampaignExportsQuery,
  useGetSchedulerFileQuery,

  // Scheduler
  useLazyGetSchedulerFileQuery,
  useLazyGetSchedulerLogQuery,
  useLazyGetSchedulerHistoryQuery,
  useGetSenderNumberQuery,
  useLazyGetSchedulersQuery,
  useGetSchedulersQuery,
  useGetSchedulerDetailQuery,
  useGetSchedulerHistoryQuery,
  useGetSchedulerLogQuery,
  useDeleteFileSchedulerMutation,

  // Upload History
  useGetUploadHistoriesQuery,

  // mutation
  useCancelCampaignMutation,
  useSubscribeContactMutation,
  useUnSubscribeContactMutation,
  useGetSegmentContactWithPaginateMutateMutation,
  useGetContactsByGroupMutateMutation,
  useDeleteContactByGroupMutation,
  useVerifyOtpMutation,
  useUpdateOwnerStatusPageSubscribeMutation,
  useCreateTagMutation,
  useDeleteSchedulerMutation,
  usePauseSchedulerMutation,
  useCreateSchedulerFileMutation,
  useCreateSummarySchedulerFileMutation,
  useUpdateAccountTypeRequestMutation,
  useUpdate2FASettingMutation,
  useTestWebhookMutation,
  useAttachTagToSegmentMutation,
  useRescheduleCampaignMutation,

  // webhook security
  useCreateWebhookSecurityMutation,
  useUpdateWebhookSecurityMutation,
  useLazyGetWebhookSecurityQuery,

  // postpaid send draft invoice
  useSendDraftInvoiceToCustomerMutation,

  useLazyGetCreditTransactionExportDataQuery,
  useLazyGetSingleReceiverMessageExportQuery,

  useGetAdminMongoScaleQuery,
  useAdminUpdateGlobalSettingMutation,
  usePostAdminMongoScaleMutation,
  useAdminGetGlobalSettingQuery,

  // ghl
  useUpdateGhlSenderMutation,
  useImportGhlContactMutation,
  useExportGhlContactMutation,

  //jobadder
  useUpdateEventTypeMutation,
  useUpdateWebhookMutation,
  useImportJobAdderContactMutation,
  useExportJobAdderContactMutation,
  useSyncPartnerActionMutation,

  //hubSpot
  useImportHubSpotContactMutation,
  useExportHubSpotContactMutation,
  useUpdateHubspotSenderMutation,
  useUpdatePhoneNumberFieldMutation,

  // activecampaign
  useUpdateActiveCampaignSenderMutation,
  useImportActiveCampaignContactMutation,
  useExportActiveCampaignContactMutation,

  // sender number
  useLazyGetDedicatedNumberQuery,
  useLazyGetCustomNumberQuery,
  useLazyGetBusinessNumberQuery,

  // commisionAdmin
  useGetUserOrderAdminSummaryQuery,
  useGetUserOrderAdminListQuery,
  useAdminUpdateCommissionMutation,
  useAdminUpdateUserCOGMutation,
  useLazyExportUserOrderAdminListQuery,
  useGetUserSalesTargetQuery,
  useUpdateUserSalesTargetMutation,
  useLazyGetSalesCommissionListQuery,

  // app Integration
  useActivateIntegrationAppMutation,
  useDeactivateIntegrationAppMutation,
  useUpdateIntegrationAppDataMutation,

  // admin
  useAdminUpdateBulkGatewayMessageMutation,
  useGetGatewayProviderQuery,
  useAdminUpdateSmsStatusMutation,
  useAdminUpdateSalesTypeMutation,
  useAdminUpdateUserTagMutation,
  useAdminUpdateUserVerifiedEmailMutation,

  useAssignListToAnotherListMutation,
  useMoveListToAnotherListMutation,
  useLazyGetFileSchedulerMessageExportQuery,
  useGetCampaignReportCustomerClicksListQuery,
  useAdminAssignedUserbySuperAdminMutation,
  useAssignCostumerCouponsMutation,

  // notification
  useNotificationMarkAllReadsMutation,
  useGetNotificationDetailQuery,
  useUpdateBounceNotificationSettingMutation,
  useUpdateUnsubscribeNotificationSettingMutation,
  useUpdateNotificationSettingsMutation,
  useLazyGetAllApprovalNotificationQuery,
  useLazyExportAllApprovalNotificationQuery,
  useUpdateSoundNotificationMutation,
  useLazyGetSoundNotificationSettingQuery,

  // coupon rate
  useLazyGetDefaultCouponRateQuery,
  useUpdateBillingAddresMutation,
  useLazyGetCustomerCouponsQuery,
  useDeleteCostumerCouponsMutation,
  useUpdateStatusUsedCouponMutation,

  // Sales KPI
  useLazyGetSalesPerformanceListQuery,
  useLazyGetSalesPotentialChurnUserQuery,

  //QM
  useCancelScheduleQuickMessageMutation,
  useSendQMMutation,
  useLazyGetInboxMessagesQuery,
  useLazyGetInboxMessageDetailsQuery,

  // New Conversation API
  useGetConversationsQuery,
  useLazyGetConversationsQuery,
  useGetConversationBadgeQuery,
  useLazyGetConversationBadgeQuery,
  useSearchConversationsQuery,
  useLazySearchConversationsQuery,
  useGetConversationByIdQuery,
  useLazyGetConversationByIdQuery,
  useGetConversationMessagesQuery,
  useLazyGetConversationMessagesQuery,
  useUpdateConversationFlagsMutation,
  useBulkUpdateConversationFlagsMutation,

  useGetAllInprogressCampaignCountQuery,
  // system alert
  useCreateSystemAlertMutation,
  useGetActiveSystemAlertQuery,
  useLazyGetAllSystemAlertQuery,
  useLazyDeleteSystemAlertQuery,

  // user
  useGetAllUserAccountQuery,

  // User Survey
  useLazyGetAllUserSurveyQuery,
  useLazyGetAllUserSurveyGraphQuery,

  // Invite Link
  useSendInviteLinkMutation,
  useVerifyInviteTokenMutation,
  useSetupAccountFromInviteMutation,
  useGenererateInviteLinkMutation,

  // Shared Number

  // How-to-videos
  useGetHowToVideosQuery,
  useLazyGetHowToVideosQuery,
  useGetHowToVideoByIdQuery,
  useLazyGetHowToVideoByIdQuery,
  useCreateHowToVideoMutation,
  useUpdateHowToVideoMutation,
  useDeleteHowToVideoMutation,
  useToggleHowToVideoStatusMutation,
  useLazyGetHowToVideosAdminQuery,
  useLazyGetHowToVideosByFeatureQuery,

  // Opt-out fraud keywords
  useLazyGetOptOutFraudKeywordsQuery,
  useUpdateOptOutFraudKeywordsMutation,

  // Shared Number
  useGetSharedNumberListQuery,
  useCreateSharedNumberMutation,
  useUpdateSharedNumberMutation,
  useDeleteSharedNumberMutation,

  // Gateway Configs
  useGetGatewayConfigsQuery,
  useCreateGatewayConfigMutation,
  useUpdateGatewayConfigMutation,
  useDeleteGatewayConfigMutation,

  // User Profile
  useGetUserProfileDetailsQuery,

  // DN Grace Period Setting
  useLazyGetDnGracePeriodSettingQuery,
  useUpdateDnGracePeriodSettingMutation,

  // DN Custom Price Setting (User facing)
  useLazyGetDnCustomPriceSettingQuery,

  // User Billing DN Type
  useUpdateUserBillingDNTypeMutation,

  // Dedicated Number Custom Price
  useLazyGetDedicatedNumberCustomPriceQuery,
  useUpdateDedicatedNumberCustomPriceMutation,
  useDeleteDedicatedNumberCustomPriceMutation,

  // gateway provider
  useGetAdminGatewayProviderQuery,
  useCreateGatewayProviderMutation,

  // Custom Opt-Out Page Branding
  useLazyGetCustomOptOutQuery,
  useUpdateCustomOptOutMutation,

  // Gateway to Conversations Migration
  useMigrateGatewayToConversationsMutation,

  // Admin Dashboard RTK Query hooks
  useGetAdminDashboardActivityOverviewCountQuery,
  useGetAdminDashboardSalesContactsCountQuery,
  useGetAdminDashboardMessageSentWithNewClientsBarGraphQuery,
} = cellcastApi
